import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useToast } from './ui';
import { UploadButton, uploadFiles } from './Upload';
import { DeletionNotice } from '../pages/Delivery';
import { LegalPendingNotice } from '../pages/Legal';

function Nav({ to, children, count }: { to: string; children: ReactNode; count?: number }) {
  return <NavLink to={to} end={to === '/'}>{children}{count ? <span className="count">{count}</span> : null}</NavLink>;
}

/** Aviso permanente del entorno de prueba (el texto con la fecha exacta de borrado llega en el hito 2). */
export function PruebaBanner() {
  const s = useSession();
  const i = s.me?.instance;
  const fecha = i?.trialExpiresAt ? new Date(i.trialExpiresAt).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' }) : null;
  const l = i?.limites;
  return (
    <div className="alert info" style={{ marginBottom: 12 }} role="note">
      <div>
        <strong>Esto es una prueba de Conforme.</strong>{' '}
        {fecha ? `Los datos que introduces se eliminarán el ${fecha}` : 'Los datos que introduces se eliminan a los 7 días'} y no se hace copia de seguridad.
        {l ? ` Máximo ${l.empresas} clientes, ${l.proveedoresPorEmpresa} proveedores por cliente y ${l.facturas} facturas, con la regla de dos facturas normales por una con problema.` : ''}{' '}
        Si quieres contratar el servicio, tendrás que crear tu cuenta real.{' '}
        <a href="/api/prueba/informe" download>Descargar informe de resultados</a>
      </div>
    </div>
  );
}

export function Layout() {
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [mine, setMine] = useState(0);
  const [dragging, setDragging] = useState(false);
  const me = s.me!;

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get(`/invoices?pageSize=1${s.companyId ? `&companyId=${s.companyId}` : ''}`).then((r) => alive && setCounts(r.counts)).catch(() => {});
      api.get(`/invoices?pageSize=1&assignedToMe=true&status=pending_approval${s.companyId ? `&companyId=${s.companyId}` : ''}`).then((r) => alive && setMine(r.total)).catch(() => {});
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [s.companyId]);

  // Arrastrar y soltar en cualquier parte de la aplicación
  useEffect(() => {
    let depth = 0;
    const enter = (e: DragEvent) => { if (e.dataTransfer?.types.includes('Files')) { depth++; setDragging(true); } };
    const leave = () => { depth = Math.max(0, depth - 1); if (!depth) setDragging(false); };
    const over = (e: DragEvent) => e.preventDefault();
    const drop = async (e: DragEvent) => {
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const files = [...(e.dataTransfer?.files ?? [])];
      if (!files.length) return;
      if (!s.companyId) { toast('Elige una empresa antes de subir facturas', 'error'); return; }
      const r = await uploadFiles(s.companyId, files).catch((err) => { toast(err.message, 'error'); return null; });
      if (r) { toast(r.summary); nav('/'); }
    };
    // El cuadro de subida detiene el «drop» antes de que llegue aquí: se oculta el cartel también en fase de captura,
    // al terminar el arrastre y con Esc, para que nunca se quede fijo.
    const hide = () => { depth = 0; setDragging(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    window.addEventListener('drop', hide, true);
    window.addEventListener('dragend', hide);
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('dragenter', enter); window.removeEventListener('dragleave', leave); window.removeEventListener('dragover', over); window.removeEventListener('drop', drop); window.removeEventListener('drop', hide, true); window.removeEventListener('dragend', hide); window.removeEventListener('keydown', esc); };
  }, [s.companyId, nav, toast]);

  const can = s.can;
  const prueba = s.prueba;
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <svg width="28" height="28" viewBox="0 0 64 64" aria-hidden="true"><rect width="64" height="64" rx="14" fill="#155e5f" /><path d="M44.7 19.3A18 18 0 1 0 44.7 44.7" fill="none" stroke="#7fe0c4" strokeWidth="6" strokeLinecap="round" /><path d="M25 32.5l5.5 5.5 11-11" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <div>Conforme<small>Cuentas a pagar</small></div>
        </div>
        <nav className="nav">
          <Nav to="/" count={(counts.review ?? 0) + (counts.received ?? 0) + (counts.processing ?? 0)}>Bandeja</Nav>
          {can('invoice.approve') && <Nav to="/aprobaciones" count={mine}>Mis aprobaciones</Nav>}
          <Nav to="/panel">Panel</Nav>
          {me.companies.length > 1 && <Nav to="/asesoria">Empresas</Nav>}
          <div className="section">Operación</div>
          <Nav to="/proveedores">Proveedores</Nav>
          {!prueba && can('invoice.export') && <Nav to="/contabilidad" count={counts.approved}>Contabilidad</Nav>}
          {!prueba && <Nav to="/factura-electronica">Factura electrónica</Nav>}
          {!prueba && s.hasModule('pagos') && (can('payment.prepare') || can('payment.approve') || can('payment.download')) && <Nav to="/pagos">Pagos</Nav>}
          {!prueba && (can('bank.import') || can('payment.mark_paid')) && <Nav to="/banco">Banco</Nav>}
          {!prueba && s.hasModule('albaranes') && <Nav to="/albaranes">Albaranes y pedidos</Nav>}
          {!prueba && <Nav to="/mensajes">Mensajes a proveedores</Nav>}
          <div className="section">Control</div>
          {!prueba && can('audit.read') && <Nav to="/auditoria">Auditoría</Nav>}
          <Nav to="/ajustes">Ajustes</Nav>
        </nav>
      </aside>
      <div className="main">
        <header className="topbar">
          <select aria-label="Empresa" value={s.companyId ?? ''} className="company-select" onChange={(e) => s.setCompanyId(e.target.value || null)} style={{ maxWidth: 320 }}>
            {me.companies.length > 1 && <option value="">Todas las empresas</option>}
            {me.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {me.organizations.length > 1 && (
            <select aria-label="Organización" value={me.organization.id} style={{ maxWidth: 220 }} onChange={async (e) => { await api.post('/auth/switch-org', { organizationId: e.target.value }); s.setCompanyId(null); await s.refresh(); nav('/'); }}>
              {me.organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <div className="grow" />
          {can('invoice.upload') && <UploadButton />}
          <span className="muted who" title={me.user.email}>{me.user.name} · {me.roleLabel}</span>
          <button className="ghost" onClick={async () => { await s.logout(); nav('/entrar'); }}>Salir</button>
        </header>
        <main className="content">{prueba && <PruebaBanner />}<LegalPendingNotice />{!prueba && <DeletionNotice />}<Outlet /></main>
      </div>
      {dragging && <div className="global-drop">Suelta aquí las facturas (PDF, imagen o XML)</div>}
    </div>
  );
}
